import { memo, useEffect, useState } from 'react';
import { isDevModeEnabled, isLocalIP } from '../utils/devMode';

const DebugOverlay = memo(({ debugStats, authInfo }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    setIsEnabled(isDevModeEnabled('debug'));

    // Add keyboard shortcut to toggle minimize (Ctrl+D)
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === 'd' && isDevModeEnabled('debug')) {
        setIsMinimized(prev => !prev);
      }
    };

    if (isDevModeEnabled('debug')) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, []);

  if (!isEnabled) {
    return null;
  }

  const { totalGames, completedGames, loadingGames, queuedGames, activeRequests, errors, avgRequestTime } = debugStats || {};
  const completionPercentage = totalGames > 0 ? ((completedGames / totalGames) * 100).toFixed(1) : 0;

  // Minimized view
  if (isMinimized) {
    const authStatus = authInfo?.isAuthenticated
      ? (authInfo?.hasActiveSubscription ? '✅' : '⚠️')
      : '❌';
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '5px',
          fontSize: '12px',
          fontFamily: 'monospace',
          zIndex: 9999,
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
        }}
        onClick={() => setIsMinimized(false)}
        title="Click to expand (or press Ctrl+D)"
      >
        {authStatus} {debugStats ? `🎯 ${completionPercentage}%` : 'Debug'}
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      right: '10px',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      fontFamily: 'monospace',
      zIndex: 9999,
      minWidth: '200px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
    }}>
      <div style={{
        fontWeight: 'bold',
        marginBottom: '5px',
        color: '#4CAF50',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>🎯 Debug Info</span>
        <span
          style={{
            cursor: 'pointer',
            fontSize: '10px',
            opacity: 0.7
          }}
          onClick={() => setIsMinimized(true)}
          title="Minimize (or press Ctrl+D)"
        >
          ▼
        </span>
      </div>

      {/* Auth Section */}
      {authInfo && (
        <>
          <div style={{
            marginTop: '8px',
            marginBottom: '8px',
            padding: '5px 0',
            borderBottom: '1px solid #555',
            fontWeight: 'bold',
            color: '#FF9800'
          }}>
            👤 Authentication
          </div>

          <div style={{ marginBottom: '3px' }}>
            <span style={{ color: '#2196F3' }}>Authenticated:</span>{' '}
            <span style={{ color: authInfo.isAuthenticated ? '#4CAF50' : '#F44336' }}>
              {authInfo.isAuthenticated ? '✅ Yes' : '❌ No'}
            </span>
          </div>

          {authInfo.isAuthenticated && authInfo.user && (
            <>
              <div style={{ marginBottom: '3px' }}>
                <span style={{ color: '#2196F3' }}>User:</span> {authInfo.user.email || authInfo.user.username || 'N/A'}
              </div>

              <div style={{ marginBottom: '3px' }}>
                <span style={{ color: '#2196F3' }}>Tier:</span> {authInfo.user.subscriptionTier || 'None'}
              </div>

              <div style={{ marginBottom: '3px' }}>
                <span style={{ color: '#2196F3' }}>Status:</span>{' '}
                <span style={{ color: authInfo.user.subscriptionStatus === 'active' ? '#4CAF50' : '#F44336' }}>
                  {authInfo.user.subscriptionStatus || 'N/A'}
                </span>
              </div>

              <div style={{ marginBottom: '3px' }}>
                <span style={{ color: '#2196F3' }}>Has Active Sub:</span>{' '}
                <span style={{ color: authInfo.hasActiveSubscription ? '#4CAF50' : '#F44336' }}>
                  {authInfo.hasActiveSubscription ? '✅ Yes' : '❌ No'}
                </span>
              </div>
            </>
          )}
        </>
      )}

      {/* Odds Loading Section */}
      {debugStats && (
        <>
          <div style={{
            marginTop: '8px',
            marginBottom: '5px',
            padding: '5px 0',
            borderBottom: '1px solid #555',
            fontWeight: 'bold',
            color: '#FF9800'
          }}>
            📊 Odds Loading
          </div>

          <div style={{ marginBottom: '3px' }}>
            <span style={{ color: '#2196F3' }}>Total Games:</span> {totalGames}
          </div>
      
      <div style={{ marginBottom: '3px' }}>
        <span style={{ color: '#4CAF50' }}>Completed:</span> {completedGames}
      </div>
      
      <div style={{ marginBottom: '3px' }}>
        <span style={{ color: '#FF9800' }}>Loading:</span> {loadingGames}
      </div>
      
      <div style={{ marginBottom: '3px' }}>
        <span style={{ color: '#9C27B0' }}>Queued:</span> {queuedGames}
      </div>
      
      <div style={{ marginBottom: '3px' }}>
        <span style={{ color: '#00BCD4' }}>Active:</span> {activeRequests}
      </div>
      
      {errors > 0 && (
        <div style={{ marginBottom: '3px' }}>
          <span style={{ color: '#F44336' }}>Errors:</span> {errors}
        </div>
      )}
      
      <div style={{ marginBottom: '3px' }}>
        <span style={{ color: '#FFC107' }}>Avg Time:</span> {avgRequestTime || 0}ms
      </div>
      
      <div style={{ 
        marginTop: '8px',
        padding: '3px 0',
        borderTop: '1px solid #555'
      }}>
        <div style={{ 
          color: completionPercentage === '100.0' ? '#4CAF50' : '#FF9800',
          fontWeight: 'bold'
        }}>
          Progress: {completionPercentage}%
        </div>
      </div>
      
      {/* Progress bar */}
      <div style={{
        width: '100%',
        height: '6px',
        backgroundColor: '#333',
        borderRadius: '3px',
        marginTop: '5px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${completionPercentage}%`,
          height: '100%',
          backgroundColor: completionPercentage === '100.0' ? '#4CAF50' : '#FF9800',
          transition: 'width 0.3s ease'
        }}></div>
      </div>
      
          {/* Status indicator */}
          <div style={{
            marginTop: '5px',
            fontSize: '11px',
            color: '#ccc'
          }}>
            {activeRequests > 0 ? '🔄 Loading...' :
             queuedGames > 0 ? '⏳ Queued...' :
             completionPercentage === '100.0' ? '✅ Complete' : '⏸️ Idle'}
          </div>
        </>
      )}
    </div>
  );
});

export default DebugOverlay;